/**
 * Hugging Face sign-in helpers shared by the entry routes.
 *
 * The HF token is owned by the SDK / host shell and lives in `sessionStorage`
 * (`hf_token`, `hf_username`, `hf_token_expires`; tab-scoped). We only ever
 * READ it here to authorise the realtime voice session, never log it, and the
 * one write is the local-dev shortcut below (VITE_HF_TOKEN), mirroring
 * tfrere/minimal-conversation's dispatch.ts (MIT).
 */

export const APP_NAME = "Reachy Study Sidekick";
export const APP_EMOJI = "🧮";
export const APP_ICON_URL = "/icon.svg";

const TOKEN_KEY = "hf_token";
const USER_KEY = "hf_username";
const EXPIRES_KEY = "hf_token_expires";

/**
 * Local-dev token from `.env.local` (both VITE_HF_TOKEN and VITE_HF_USERNAME set), else undefined.
 *
 * DEV only: Vite also loads `.env.local` for `vite build`, so without the
 * `import.meta.env.DEV` guard a local build would compile the personal token
 * into dist/ (and its source maps) and hand it to every visitor. Inside
 * `if (import.meta.env.DEV)` the read is dead code in production and is
 * dropped; vite.config.ts additionally refuses to build while it is set.
 */
export function devToken(): { token: string; userName: string } | undefined {
  if (import.meta.env.DEV) {
    const token = import.meta.env.VITE_HF_TOKEN;
    const userName = import.meta.env.VITE_HF_USERNAME;
    if (token && userName) return { token, userName };
  }
  return undefined;
}

/** Optional OAuth client id for local testing of the real redirect flow (DEV only). */
export function devClientId(): string | undefined {
  if (import.meta.env.DEV) return import.meta.env.VITE_HF_OAUTH_CLIENT_ID || undefined;
  return undefined;
}

/**
 * Seed the SDK token store with the dev token so `authenticate()` resolves
 * without a redirect. All three keys are needed by the SDK's cache check.
 */
export function seedDevToken(dev: { token: string; userName: string }): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, dev.token);
    sessionStorage.setItem(USER_KEY, dev.userName);
    sessionStorage.setItem(EXPIRES_KEY, new Date(Date.now() + 24 * 3600 * 1000).toISOString());
  } catch {
    /* storage blocked: the voice falls back to text mode */
  }
}

/** Current HF token from the SDK's session store, or null. */
export function readHfToken(): string | null {
  try {
    const t = sessionStorage.getItem(TOKEN_KEY);
    return t && t.trim() ? t : null;
  } catch {
    return null;
  }
}

/** Current HF username from the SDK's session store, or null. */
export function readHfUser(): string | null {
  try {
    return sessionStorage.getItem(USER_KEY) || null;
  } catch {
    return null;
  }
}
